import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { 
  Palette, Building2, Eye, Save, Undo, Check, 
  Upload, Image as ImageIcon, Type, Layout, Paintbrush
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminThemes() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [theme, setTheme] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const fetchTenants = useCallback(async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.get(`${API}/platform/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPresets = useCallback(async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.get(`${API}/branding/admin/theme-presets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPresets(response.data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchTenants();
    fetchPresets();
  }, [fetchTenants, fetchPresets]);

  const fetchTenantTheme = async (tenantId) => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.get(`${API}/branding/admin/tenants/${tenantId}/theme`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTheme(response.data.theme);
      setSelectedTenant(tenants.find(t => t.id === tenantId));
    } catch (error) {
      toast.error('Failed to fetch theme');
    }
  };

  const handleSaveTheme = async () => {
    if (!selectedTenant || !theme) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.put(`${API}/branding/admin/tenants/${selectedTenant.id}/theme`, theme, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Theme saved successfully');
    } catch (error) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setTheme({
      ...theme,
      ...preset.theme
    });
    toast.success(`Applied "${preset.name}" preset`);
  };

  const updateTheme = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Theme Customization
        </h1>
        <p className="text-slate-400 mt-1">Customize branding and themes for your tenants</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tenant Selection */}
        <Card className="bg-slate-900 border-slate-700 lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-violet-400" />
              Select Tenant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-96 overflow-y-auto">
            {tenants.map((tenant) => (
              <button
                key={tenant.id}
                onClick={() => fetchTenantTheme(tenant.id)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedTenant?.id === tenant.id
                    ? 'bg-violet-600/20 border-violet-500 text-white'
                    : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
              >
                <p className="font-medium text-sm">{tenant.name}</p>
                <p className="text-xs text-slate-500">{tenant.slug}</p>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Theme Editor */}
        <Card className="bg-slate-900 border-slate-700 lg:col-span-3">
          {!selectedTenant ? (
            <CardContent className="flex items-center justify-center h-96">
              <div className="text-center">
                <Palette className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Select a tenant to customize their theme</p>
              </div>
            </CardContent>
          ) : (
            <>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white text-lg">{selectedTenant.name}</CardTitle>
                  <CardDescription>Customize branding and visual theme</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-slate-600 text-slate-300"
                    onClick={() => setPreviewMode(true)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Preview
                  </Button>
                  <Button
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={handleSaveTheme}
                    disabled={saving}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Theme'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="branding" className="w-full">
                  <TabsList className="grid w-full grid-cols-4 bg-slate-800">
                    <TabsTrigger value="branding" className="data-[state=active]:bg-violet-600">Branding</TabsTrigger>
                    <TabsTrigger value="colors" className="data-[state=active]:bg-violet-600">Colors</TabsTrigger>
                    <TabsTrigger value="typography" className="data-[state=active]:bg-violet-600">Typography</TabsTrigger>
                    <TabsTrigger value="presets" className="data-[state=active]:bg-violet-600">Presets</TabsTrigger>
                  </TabsList>

                  {/* Branding Tab */}
                  <TabsContent value="branding" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Company Name</Label>
                        <Input
                          value={theme?.company_name || ''}
                          onChange={(e) => updateTheme('company_name', e.target.value)}
                          className="mt-2 bg-slate-800 border-slate-600 text-white"
                          placeholder="Company Name"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Logo URL</Label>
                        <Input
                          value={theme?.logo_url || ''}
                          onChange={(e) => updateTheme('logo_url', e.target.value)}
                          className="mt-2 bg-slate-800 border-slate-600 text-white"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Dark Logo URL</Label>
                        <Input
                          value={theme?.logo_dark_url || ''}
                          onChange={(e) => updateTheme('logo_dark_url', e.target.value)}
                          className="mt-2 bg-slate-800 border-slate-600 text-white"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Favicon URL</Label>
                        <Input
                          value={theme?.favicon_url || ''}
                          onChange={(e) => updateTheme('favicon_url', e.target.value)}
                          className="mt-2 bg-slate-800 border-slate-600 text-white"
                          placeholder="https://..."
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-300 text-xs font-bold uppercase">Login Background URL</Label>
                        <Input
                          value={theme?.login_background_url || ''}
                          onChange={(e) => updateTheme('login_background_url', e.target.value)}
                          className="mt-2 bg-slate-800 border-slate-600 text-white"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Colors Tab */}
                  <TabsContent value="colors" className="space-y-4 mt-4">
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { key: 'primary_color', label: 'Primary' },
                        { key: 'secondary_color', label: 'Secondary' },
                        { key: 'accent_color', label: 'Accent' },
                        { key: 'background_color', label: 'Background' },
                        { key: 'surface_color', label: 'Surface' },
                        { key: 'text_primary', label: 'Text Primary' },
                        { key: 'text_secondary', label: 'Text Secondary' },
                        { key: 'success_color', label: 'Success' },
                        { key: 'warning_color', label: 'Warning' },
                        { key: 'error_color', label: 'Error' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-3">
                          <input
                            type="color"
                            value={theme?.[key] || '#3b82f6'}
                            onChange={(e) => updateTheme(key, e.target.value)}
                            className="w-10 h-10 rounded cursor-pointer border-0"
                          />
                          <div>
                            <Label className="text-slate-300 text-xs font-bold uppercase">{label}</Label>
                            <Input
                              value={theme?.[key] || ''}
                              onChange={(e) => updateTheme(key, e.target.value)}
                              className="mt-1 bg-slate-800 border-slate-600 text-white h-8 text-xs w-24"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  {/* Typography Tab */}
                  <TabsContent value="typography" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Body Font</Label>
                        <Select value={theme?.font_family || 'DM Sans'} onValueChange={(v) => updateTheme('font_family', v)}>
                          <SelectTrigger className="mt-2 bg-slate-800 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                            <SelectItem value="DM Sans" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">DM Sans</SelectItem>
                            <SelectItem value="Inter" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Inter</SelectItem>
                            <SelectItem value="Poppins" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Poppins</SelectItem>
                            <SelectItem value="Open Sans" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Open Sans</SelectItem>
                            <SelectItem value="Roboto" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Roboto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Heading Font</Label>
                        <Select value={theme?.heading_font || 'Manrope'} onValueChange={(v) => updateTheme('heading_font', v)}>
                          <SelectTrigger className="mt-2 bg-slate-800 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                            <SelectItem value="Manrope" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Manrope</SelectItem>
                            <SelectItem value="Montserrat" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Montserrat</SelectItem>
                            <SelectItem value="Playfair Display" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Playfair Display</SelectItem>
                            <SelectItem value="Source Sans Pro" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Source Sans Pro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Border Radius</Label>
                        <Select value={theme?.border_radius || '8px'} onValueChange={(v) => updateTheme('border_radius', v)}>
                          <SelectTrigger className="mt-2 bg-slate-800 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                            <SelectItem value="0px" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Sharp (0px)</SelectItem>
                            <SelectItem value="4px" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Subtle (4px)</SelectItem>
                            <SelectItem value="8px" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Default (8px)</SelectItem>
                            <SelectItem value="12px" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Rounded (12px)</SelectItem>
                            <SelectItem value="16px" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">More Rounded (16px)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300 text-xs font-bold uppercase">Sidebar Style</Label>
                        <Select value={theme?.sidebar_style || 'dark'} onValueChange={(v) => updateTheme('sidebar_style', v)}>
                          <SelectTrigger className="mt-2 bg-slate-800 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                            <SelectItem value="dark" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Dark</SelectItem>
                            <SelectItem value="light" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Light</SelectItem>
                            <SelectItem value="gradient" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Gradient</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs font-bold uppercase">Custom CSS (Advanced)</Label>
                      <Textarea
                        value={theme?.custom_css || ''}
                        onChange={(e) => updateTheme('custom_css', e.target.value)}
                        className="mt-2 bg-slate-800 border-slate-600 text-white font-mono text-xs"
                        rows={5}
                        placeholder="/* Add custom CSS here */"
                      />
                    </div>
                  </TabsContent>

                  {/* Presets Tab */}
                  <TabsContent value="presets" className="mt-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {presets.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => applyPreset(preset)}
                          className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-violet-500 transition-colors text-left"
                        >
                          <div className="flex gap-1 mb-3">
                            {preset.preview_colors.map((color, idx) => (
                              <div
                                key={`supe-${idx}`}
                                className="w-6 h-6 rounded"
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                          <p className="text-white font-medium text-sm">{preset.name}</p>
                          <p className="text-slate-500 text-xs mt-1">Click to apply</p>
                        </button>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewMode} onOpenChange={setPreviewMode}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Theme Preview - {selectedTenant?.name}</DialogTitle>
          </DialogHeader>
          {theme && (
            <div 
              className="p-6 rounded-lg"
              style={{ 
                backgroundColor: theme.background_color,
                fontFamily: theme.font_family
              }}
            >
              <div 
                className="p-4 rounded-lg mb-4"
                style={{ backgroundColor: theme.surface_color }}
              >
                <h2 
                  style={{ 
                    color: theme.text_primary,
                    fontFamily: theme.heading_font
                  }}
                  className="text-xl font-bold mb-2"
                >
                  {theme.company_name || selectedTenant?.name}
                </h2>
                <p style={{ color: theme.text_secondary }}>
                  Sample preview of how the tenant's interface will look
                </p>
              </div>
              <div className="flex gap-3">
                <button 
                  className="px-4 py-2 rounded text-white font-medium"
                  style={{ 
                    backgroundColor: theme.primary_color,
                    borderRadius: theme.border_radius
                  }}
                >
                  Primary Button
                </button>
                <button 
                  className="px-4 py-2 rounded font-medium border"
                  style={{ 
                    borderColor: theme.secondary_color,
                    color: theme.secondary_color,
                    borderRadius: theme.border_radius
                  }}
                >
                  Secondary Button
                </button>
                <button 
                  className="px-4 py-2 rounded text-white font-medium"
                  style={{ 
                    backgroundColor: theme.accent_color,
                    borderRadius: theme.border_radius
                  }}
                >
                  Accent Button
                </button>
              </div>
              <div className="flex gap-3 mt-4">
                <span className="px-3 py-1 rounded text-white text-sm" style={{ backgroundColor: theme.success_color }}>Success</span>
                <span className="px-3 py-1 rounded text-white text-sm" style={{ backgroundColor: theme.warning_color }}>Warning</span>
                <span className="px-3 py-1 rounded text-white text-sm" style={{ backgroundColor: theme.error_color }}>Error</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
